import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Navigation as NavigationIcon
} from '@mui/icons-material';
import PageNavigator from './PageNavigator';
import ZoomControls from './ZoomControls';
import ProgressBar from './ProgressBar';

const DocumentNavigator = ({
  text = '',
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onZoomChange,
  onSearch,
  searchResults = [],
  currentSearchIndex = -1,
  documentType = 'text', // 'text' or 'pdf'
  scrollPosition = 0,
  documentHeight = 0
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [isOpen, setIsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  
  // Auto-close on mobile when not in use
  useEffect(() => {
    if (isMobile && activePanel === null) {
      const timer = setTimeout(() => setIsOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, activePanel]);
  
  // Listen for custom event to open navigator
  useEffect(() => {
    const handleOpenNavigator = () => {
      if (!isOpen) {
        setIsOpen(true);
        // Default to zoom panel when opening navigator
        setActivePanel('zoom');
      }
    };
    
    window.addEventListener('openNavigator', handleOpenNavigator);
    return () => window.removeEventListener('openNavigator', handleOpenNavigator);
  }, [isOpen]);

  const handlePanelToggle = (panelName) => {
    // Only open panels, never close them
    // If clicking the same panel that's already active, keep it open
    if (!isOpen) setIsOpen(true);
    if (activePanel !== panelName) {
      setActivePanel(panelName);
    }
  };

  const navigatorWidth = isMobile ? '100%' : '320px';

  const getNavigatorStyle = () => {
    const baseStyle = {
      position: 'fixed',
      zIndex: 1200,
      transition: 'all 0.3s ease-in-out',
      maxHeight: isMobile ? '50vh' : '100vh',
      overflow: 'hidden'
    };

    if (isMobile) {
      return {
        ...baseStyle,
        bottom: isOpen ? 0 : '-50vh',
        left: 0,
        right: 0,
        width: '100%',
        borderRadius: '16px 16px 0 0',
        boxShadow: theme.shadows[8]
      };
    } else {
      return {
        ...baseStyle,
        top: 80,
        right: isOpen ? 0 : -320,
        width: navigatorWidth,
        height: 'calc(100vh - 100px)',
        borderRadius: '16px 0 0 16px',
        boxShadow: theme.shadows[4]
      };
    }
  };

  const FloatingToggle = () => (
    <Box
      sx={{
        position: 'fixed',
        top: isMobile ? 'auto' : 120,
        bottom: isMobile ? 20 : 'auto',
        right: 20,
        zIndex: 1300,
        display: isOpen ? 'none' : 'block'
      }}
    >
      <Tooltip title="Open Navigator" placement="left">
        <IconButton
          data-testid="navigator-toggle"
          onClick={() => {
            setIsOpen(true);
            // Default to zoom panel when opening navigator
            setActivePanel('zoom');
          }}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            boxShadow: theme.shadows[4],
            width: 56,
            height: 56
          }}
        >
          <NavigationIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <>
      <FloatingToggle />
      
      <Paper
        elevation={8}
        sx={{
          ...getNavigatorStyle(),
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            minHeight: 64
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NavigationIcon />
            <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              Navigator
            </Box>
          </Box>
          
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress Bar */}
        <ProgressBar
          currentPage={currentPage}
          totalPages={totalPages}
          scrollPosition={scrollPosition}
          documentHeight={documentHeight}
          documentType={documentType}
        />

        {/* Control Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            p: 1.5,
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Tooltip title="Navigation">
            <IconButton
              onClick={() => handlePanelToggle('pages')}
              sx={{
                backgroundColor: activePanel === 'pages' 
                  ? theme.palette.primary.main 
                  : theme.palette.mode === 'dark' 
                    ? theme.palette.grey[700] 
                    : theme.palette.grey[100],
                color: activePanel === 'pages' ? 'white' : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: activePanel === 'pages' 
                    ? theme.palette.primary.dark 
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[600]
                      : theme.palette.grey[200],
                },
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 44,
                minHeight: 44
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Zoom">
            <IconButton
              onClick={() => handlePanelToggle('zoom')}
              sx={{
                backgroundColor: activePanel === 'zoom' 
                  ? theme.palette.primary.main 
                  : theme.palette.mode === 'dark' 
                    ? theme.palette.grey[700] 
                    : theme.palette.grey[100],
                color: activePanel === 'zoom' ? 'white' : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: activePanel === 'zoom' 
                    ? theme.palette.primary.dark 
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[600]
                      : theme.palette.grey[200],
                },
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 44,
                minHeight: 44
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Panels */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <Collapse in={activePanel === 'pages'}>
            <PageNavigator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              documentType={documentType}
              text={text}
              onSearch={onSearch}
              searchResults={searchResults}
              currentSearchIndex={currentSearchIndex}
            />
          </Collapse>

          <Collapse in={activePanel === 'zoom'}>
            <ZoomControls
              onZoomChange={onZoomChange}
            />
          </Collapse>
        </Box>
      </Paper>
    </>
  );
};

export default DocumentNavigator;
